/* eslint-disable no-return-assign, sort-keys, no-underscore-dangle */
import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import FaCalendar from 'react-icons/lib/fa/calendar';
import FaClose from 'react-icons/lib/fa/close';

import {
    Skeleton,
    SkeletonBar,
} from 'wanderu-animations';
import {
    CalendarIcon,
} from 'wanderu-components';
import { i18nMomentPropType } from 'wanderu-i18n';
import { generateFakeOnChangeEvent } from 'wanderu-utils';

import Calendar from './Calendar/Calendar';

// eslint-disable-next-line css-modules/no-unused-class
import styles from './DatePicker.scss';
export default class DatePicker extends React.Component {
    static propTypes = {
        locale: PropTypes.string.isRequired,
        onChange: PropTypes.func.isRequired,
        onFocus: PropTypes.func,
        onBlur: PropTypes.func,
        placeholder: PropTypes.string,
        dateData: PropTypes.object,
        currentDate: i18nMomentPropType,
        minDate: i18nMomentPropType,
        maxDate: i18nMomentPropType,
        clearable: PropTypes.bool,
        underline: PropTypes.bool,
        icon: PropTypes.bool,
        iconClass: PropTypes.string,
        containerClass: PropTypes.string,
        inputClass: PropTypes.string,
        textClass: PropTypes.string,
        highlightClass: PropTypes.string,
        today: PropTypes.string,
        required: PropTypes.bool,
        showPin: PropTypes.bool,
    };

    static defaultProps = {
        onFocus() { },
        onBlur() { },
        required: false,
        showPin: false,
    };

    constructor(props) {
        super(props);
        this.state = {
            calendarIsVisible: false,
        };
    }

    get _formattedValue() {
        const {
            currentDate,
            locale,
        } = this.props;
        if (!currentDate) {
            return '';
        }
        return currentDate.shortDayAndWeekday(locale);
    }

    _showCalendar() {
        this.setState({ calendarIsVisible: true });
        this.startListeningForOutsideInteraction();
    }

    _hideCalendar = () => {
        // eslint-disable-next-line no-unused-expressions
        this.input?.blur();
    };

    startListeningForOutsideInteraction() {
        document.addEventListener('touchstart', this.onOutsideTouchStart);
        window.addEventListener('scroll', this.onScroll);
        document.addEventListener('touchend', this.onOutsideTouchEnd);
    }

    stopListeningForOutsideInteraction() {
        document.removeEventListener('touchstart', this.onOutsideTouchStart);
        window.removeEventListener('scroll', this.onScroll);
        document.removeEventListener('touchend', this.onOutsideTouchEnd);
    }

    onOutsideTouchStart = (e) => {
        this.scrolled = false;
    };

    onScroll = () => this.scrolled = true;

    onOutsideTouchEnd = (e) => {
        const nodeList = document.querySelectorAll('.' + styles.container);
        const nodes = Array.prototype.slice.call(nodeList);
        if (nodes.some((node) => node.contains(e.target)) || this.scrolled) {
            return;
        }
        this.scrolled = false;
        this._hideCalendar();
    };

    // eslint-disable-next-line consistent-return
    _onMouseDown = (e) => {
        if (!this.state.calendarIsVisible) {
            return this._showCalendar();
        }
        if (e.target === this.input) {
            this._hideCalendar();
        }
        e.preventDefault();
        e.stopPropagation();
    };

    _onFocus = () => {
        this._showCalendar();
        this.props.onFocus();
    };

    _onBlur = () => {
        this.stopListeningForOutsideInteraction();
        this.setState({ calendarIsVisible: false });
        this.props.onBlur();
    };

    _onIconClick = () => {
        this.input.focus();
    };

    _onClearClick = (e) => {
        this.input.focus();
        const fakeChangeEvent = generateFakeOnChangeEvent(e, undefined);
        this.props.onChange(fakeChangeEvent);
    };

    _onClearClickSplit = (e) => {
        this.input.focus();
        const fakeChangeEvent = generateFakeOnChangeEvent(e, undefined);
        this.props.onChange(fakeChangeEvent);
        this.setState({ calendarIsVisible: false });
    };

    render() {
        const {
            calendarClass,
            containerClass,
            highlightClass,
            iconClass,
            inputClass,
            textClass,
            underline,
            placeholder,
            icon,
            clearable,
            dateData,
            currentDate,
            minDate,
            maxDate,
            today,
            onChange,
            required,
            showPin,
            loading,
        } = this.props;

        return (
            <div
                className={classNames(
                    styles.container,
                    containerClass,
                )}
                onMouseDown={this._onMouseDown}
            >
                {showPin && <CalendarIcon className={styles.calendarPin} />}
                {underline && (
                    <span
                        className={classNames(
                            textClass,
                            styles.dummyText,
                        )}
                    >
                        {this._formattedValue || placeholder}
                    </span>
                )}
                <input
                    aria-label={placeholder}
                    aria-required={required}
                    className={classNames(
                        styles.input,
                        inputClass,
                        textClass,
                        { [styles.hideInput]: loading },
                    )}
                    onBlur={this._onBlur}
                    onFocus={this._onFocus}
                    placeholder={placeholder}
                    readOnly={true}
                    ref={(node) => this.input = node}
                    type="text"
                    value={this._formattedValue}
                />
                {loading && (
                    <Skeleton
                        className={classNames(
                            styles.input,
                            inputClass,
                            styles.skeleton,
                        )}
                    >
                        <SkeletonBar className={styles.skeletonBar} />
                    </Skeleton>
                )}
                {icon && !currentDate && (
                    <FaCalendar
                        className={classNames(
                            styles.icon,
                            iconClass,
                        )}
                        onClick={this._onIconClick}
                    />
                )}
                {clearable && currentDate && (
                    <FaClose
                        className={classNames(
                            styles.icon,
                            iconClass,
                        )}
                        onBlur={this._hideCalendar}
                        onClick={this._onClearClick}
                    />
                )}

                {this.state.calendarIsVisible && (
                    <Calendar
                        calendarClass={calendarClass}
                        dateData={dateData}
                        hideCalendar={this._hideCalendar}
                        highlightClass={highlightClass}
                        maxDate={maxDate}
                        minDate={minDate}
                        onDateChange={onChange}
                        selectedDate={currentDate || minDate}
                        today={today}
                    />
                )}
            </div>
        );
    }
}
